import { ApiResponse, GlobalStore, GridStore } from '@roc/feature-app-core';
import {
  GENERIC_ERROR_MESSAGE,
  insertIf,
  isEmptyString,
  LoanStatus,
  Roles,
} from '@roc/feature-utils';
import { BorrowerLoansService } from '@roc/feature-borrower-loans';
import { GeneralTasksService } from '../services/generalTasksService';
import {
  GeneralTasksFilters,
  GeneralTasksTab,
  TaskScope,
  TaskStatus,
} from '../types/generalTasksTypes';
import { GeneralTasksStore } from './generalTasksStore';
import { TaskColumns, TaskColumnsDefinitions } from '../utils/generalTasksColumns';

export class GeneralTasksGridStore extends GridStore {
  globalStore: GlobalStore;
  generalTasksStore: GeneralTasksStore;
  generalTasksService: GeneralTasksService;

  constructor(globalStore: GlobalStore, generalTasksStore: GeneralTasksStore, status?: TaskStatus) {
    super(() => this.fetchGeneralTasksByStatus(status));
    this.globalStore = globalStore;
    this.generalTasksStore = generalTasksStore;
    this.generalTasksService = new GeneralTasksService();
  }

  fetchGeneralTasksByStatus = async status => {
    try {
      const loanId = this.generalTasksStore.loanId;
      const lenderId = this.generalTasksStore.lenderId;
      switch (this.generalTasksStore.tab) {
        case GeneralTasksTab.ALL_TASKS:
          return await this.fetchAllTasksByAssigneeIdsGrid(loanId, status);
        case GeneralTasksTab.ALL_TASKS_FOR_LOAN:
          return await this.fetchAllTasksGridForLoan(loanId, status);
        case GeneralTasksTab.MY_TASKS:
          return await this.fetchMyTasksGrid(loanId, status, lenderId);
        case GeneralTasksTab.MY_TEAM_TASKS:
          return await this.fetchMyTasksGrid(loanId, status);
        case GeneralTasksTab.MY_OTHER_TASKS:
          return await this.fetchMyOtherTasksGrid(loanId, status, lenderId);
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  };

  fetchAllTasksGridForLoan = async (loanId, status) => {
    const resultType = this.generalTasksStore.filters[
      GeneralTasksFilters.RESULT_TYPE
    ];

    const { filters, dropdownFilters } = this.generalTasksStore.getFilterParams();
    return this.generalTasksService.getAllTasksGridForLoan(
      loanId,
      status,
      this.gridData.meta.pageNumber,
      this.gridData.meta.pageSize,
      this.gridData.meta.sortDir,
      this.gridData.meta.sortBy,
      filters,
      dropdownFilters,
      resultType
    );
  };

  fetchAllTasksByAssigneeIdsGrid = async (loanId, status) => {
    const resultType = this.generalTasksStore.filters[
      GeneralTasksFilters.RESULT_TYPE
    ];

    const { filters, dropdownFilters } = this.generalTasksStore.getFilterParams();
    return this.generalTasksService.getAllTasksByAssigneeIdsGrid(
      loanId,
      status,
      this.gridData.meta.pageNumber,
      this.gridData.meta.pageSize,
      this.gridData.meta.sortDir,
      this.gridData.meta.sortBy,
      filters,
      dropdownFilters,
      resultType
    );
  }

  fetchMyTasksGrid = async (loanId, status, lenderId?: number) => {
    const taskScope = this.generalTasksStore.filters[
      GeneralTasksFilters.TASK_SCOPE
    ];

    const resultType = this.generalTasksStore.filters[
      GeneralTasksFilters.RESULT_TYPE
    ];

    let taskScopeOptions = taskScope?.length > 0 ? taskScope.map(o => o.value).join(',') : undefined;
    if (this.generalTasksStore.isTeamScope) {
      taskScopeOptions = TaskScope.TEAM;
    }
    const { filters, dropdownFilters } = this.generalTasksStore.getFilterParams();

    return this.generalTasksService.getMyTasksGrid(
      loanId,
      status,
      this.gridData.meta.pageNumber,
      this.gridData.meta.pageSize,
      this.gridData.meta.sortDir,
      this.gridData.meta.sortBy,
      filters,
      dropdownFilters,
      null,
      taskScopeOptions,
      resultType,
      lenderId,
    );
  }

  fetchMyOtherTasksGrid = async (loanId, status, lenderId?: number) => {
    const taskScope = this.generalTasksStore.filters[
      GeneralTasksFilters.TASK_SCOPE
    ];

    let taskScopeOptions = taskScope?.length > 0 ? taskScope.map(o => o.value).join(',') : undefined;
    if (this.generalTasksStore.isTeamScope) {
      taskScopeOptions = TaskScope.TEAM;
    }
    const { filters, dropdownFilters } = this.generalTasksStore.getFilterParams();
    return this.generalTasksService.getMyOtherTasksGrid(
      loanId,
      status,
      this.gridData.meta.pageNumber,
      this.gridData.meta.pageSize,
      this.gridData.meta.sortDir,
      this.gridData.meta.sortBy,
      filters,
      dropdownFilters,
      taskScopeOptions,
      lenderId,
    );
  }

  getTaskGridColumns(role: string, supportTasksModalOpen: boolean, isLoanTasksView: boolean, tab: GeneralTasksTab, showReportedByMe: boolean, isInternalPortal: boolean, taskScopeList: TaskScope[]) {
    if (tab === GeneralTasksTab.MY_TASKS) {
      return this.getMyTaskGridColumns(role, supportTasksModalOpen, isLoanTasksView, tab, showReportedByMe, isInternalPortal, taskScopeList);
    }

    return this.getDefaultTaskGridColumns(supportTasksModalOpen, isLoanTasksView, tab, showReportedByMe, isInternalPortal, taskScopeList);
  }

  private getMyTaskGridColumns(
    role: string, supportTasksModalOpen: boolean, isLoanTasksView: boolean,
    tab: GeneralTasksTab, showReportedByMe: boolean, isInternalPortal: boolean,
    taskScopeList: TaskScope[]
  ) {
    switch (role) {
      case Roles.VALUATION_REVIEWER:
      case Roles.VALUATION_ANALYST:
      case Roles.VALUATION_QE: {
        return [
          TaskColumnsDefinitions[TaskColumns.TASK_ID],
          TaskColumnsDefinitions[TaskColumns.TITLE],
          TaskColumnsDefinitions[TaskColumns.TASK_STATUS],
          TaskColumnsDefinitions[TaskColumns.ESCALATED],
          ...insertIf((GeneralTasksTab.MY_TASKS !== tab || showReportedByMe) && !supportTasksModalOpen, [TaskColumnsDefinitions[TaskColumns.ASSIGNEE]]),
          TaskColumnsDefinitions[TaskColumns.TIME_ELAPSED],
          ...insertIf((GeneralTasksTab.MY_TASKS !== tab || !showReportedByMe) && !supportTasksModalOpen, [TaskColumnsDefinitions[TaskColumns.REPORTER]]),
          ...insertIf(!supportTasksModalOpen, [TaskColumnsDefinitions[TaskColumns.PRIORITY]]),
          TaskColumnsDefinitions[TaskColumns.CREATED_DATE],
          TaskColumnsDefinitions[TaskColumns.LAST_UPDATED_DATE],
          TaskColumnsDefinitions[TaskColumns.QUEUE_START_DATE],
          ...insertIf(!isLoanTasksView && !supportTasksModalOpen, [TaskColumnsDefinitions[TaskColumns.LOAN_ID]]),
          TaskColumnsDefinitions[TaskColumns.LOAN_STATUS],
          TaskColumnsDefinitions[TaskColumns.LOAN_SUB_STATUS],
          TaskColumnsDefinitions[TaskColumns.VALUATION_REVIEWER],
          TaskColumnsDefinitions[TaskColumns.PROPERTY_ADDRESS],
          TaskColumnsDefinitions[TaskColumns.PROPERTY_STATE],
          TaskColumnsDefinitions[TaskColumns.IV_REASON],
          TaskColumnsDefinitions[TaskColumns.TRANSACTION_TYPE],
          TaskColumnsDefinitions[TaskColumns.APPRAISAL_RECEIVED_DATE],

          TaskColumnsDefinitions[TaskColumns.APPRAISAL_PAID_DATE],
          TaskColumnsDefinitions[TaskColumns.UNDERWRITING_DATE],
          TaskColumnsDefinitions[TaskColumns.PROPERTY_CITY],
          TaskColumnsDefinitions[TaskColumns.PROPERTY_COUNTY],
          TaskColumnsDefinitions[TaskColumns.UNDERWRITER],
          TaskColumnsDefinitions[TaskColumns.APPRAISER],
          TaskColumnsDefinitions[TaskColumns.LOAN_SUB_TYPE],
          TaskColumnsDefinitions[TaskColumns.LENDER],
          TaskColumnsDefinitions[TaskColumns.LOAN_AMOUNT],
          TaskColumnsDefinitions[TaskColumns.ARV],

          ...insertIf(isInternalPortal, [TaskColumnsDefinitions[TaskColumns.ACTIONS]]),
        ];
      }
      case Roles.DRAW_REVIEW_SPECIALIST: {
        return [
          TaskColumnsDefinitions[TaskColumns.TASK_ID],
          ...insertIf(!isLoanTasksView && !supportTasksModalOpen, [TaskColumnsDefinitions[TaskColumns.LOAN_ID]]),
          TaskColumnsDefinitions[TaskColumns.TITLE],
          TaskColumnsDefinitions[TaskColumns.TASK_STATUS],
          TaskColumnsDefinitions[TaskColumns.ESCALATED],
          ...insertIf((GeneralTasksTab.MY_TASKS !== tab || showReportedByMe) && !supportTasksModalOpen, [TaskColumnsDefinitions[TaskColumns.ASSIGNEE]]),
          TaskColumnsDefinitions[TaskColumns.TIME_ELAPSED],
          ...insertIf((GeneralTasksTab.MY_TASKS !== tab || !showReportedByMe) && !supportTasksModalOpen, [TaskColumnsDefinitions[TaskColumns.REPORTER]]),
          ...insertIf(!supportTasksModalOpen, [TaskColumnsDefinitions[TaskColumns.PRIORITY]]),
          TaskColumnsDefinitions[TaskColumns.CREATED_DATE],
          TaskColumnsDefinitions[TaskColumns.LAST_UPDATED_DATE],
          TaskColumnsDefinitions[TaskColumns.LOAN_STATUS],
          TaskColumnsDefinitions[TaskColumns.LOAN_SUB_TYPE],
          TaskColumnsDefinitions[TaskColumns.QUEUE_START_DATE],
          TaskColumnsDefinitions[TaskColumns.APPRAISAL_REPORT_STATUS],
          TaskColumnsDefinitions[TaskColumns.PROPERTY_ADDRESS],
          TaskColumnsDefinitions[TaskColumns.LENDER],
          TaskColumnsDefinitions[TaskColumns.PROJECT_TYPE],
          TaskColumnsDefinitions[TaskColumns.CMT_REPORT_VERSION],
          ...insertIf(isInternalPortal, [TaskColumnsDefinitions[TaskColumns.ACTIONS]]),
        ];
      }
      default: {
        return this.getDefaultTaskGridColumns(supportTasksModalOpen, isLoanTasksView, tab, showReportedByMe, isInternalPortal, taskScopeList);
      }
    }
  }

  private getDefaultTaskGridColumns(supportTasksModalOpen: boolean, isLoanTasksView: boolean, tab: GeneralTasksTab, showReportedByMe: boolean, isInternalPortal: boolean, taskScopeList: TaskScope[]) {
    return [
      TaskColumnsDefinitions[TaskColumns.TASK_ID],
      ...insertIf(!isLoanTasksView && !supportTasksModalOpen, [TaskColumnsDefinitions[TaskColumns.LOAN_ID]]),
      TaskColumnsDefinitions[TaskColumns.TITLE],
      TaskColumnsDefinitions[TaskColumns.TASK_STATUS],
      TaskColumnsDefinitions[TaskColumns.ESCALATED],
      ...insertIf((GeneralTasksTab.MY_TASKS !== tab || showReportedByMe || taskScopeList.length === 0) && !supportTasksModalOpen, [TaskColumnsDefinitions[TaskColumns.ASSIGNEE]]),
      TaskColumnsDefinitions[TaskColumns.TIME_ELAPSED],
      ...insertIf((GeneralTasksTab.MY_TASKS !== tab || !showReportedByMe || taskScopeList.length === 0) && !supportTasksModalOpen, [TaskColumnsDefinitions[TaskColumns.REPORTER]]),
      ...insertIf(!supportTasksModalOpen, [TaskColumnsDefinitions[TaskColumns.PRIORITY]]),
      TaskColumnsDefinitions[TaskColumns.CREATED_DATE],
      TaskColumnsDefinitions[TaskColumns.LAST_UPDATED_DATE],
      ...insertIf(isInternalPortal || supportTasksModalOpen, [TaskColumnsDefinitions[TaskColumns.ACTIONS]]),
    ];
  }
}
